<template>
  <!-- TimePicker without date -->
  <DatePicker
    v-if="time"
    :inputId="id"
    appendTo="body"
    v-model="datetime12h"
    :stepMinute="15"
    timeOnly
    hourFormat="12"
    fluid
  />

  <!-- DatePicker with date -->
  <DatePicker
    v-else-if="date"
    :inputId="id"
    appendTo="body"
    v-model="datetime12h"
    fluid
    :placeholder="placeholder"
  />

  <!-- DatePicker with date + time -->
  <DatePicker
    v-else
    :inputId="id"
    appendTo="body"
    v-model="datetime12h"
    :stepMinute="15"
    hourFormat="12"
    fluid
    showTime
    :placeholder="placeholder"
  />

  <!-- Hidden field to submit ISO or date string to Rails -->
  <input type="hidden" :name="name" :value="formattedDateTime || ''" />
</template>

<script setup>
  import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
  import { DateTime } from 'luxon'
  import DatePicker from 'primevue/datepicker'
  import { useI18n } from 'vue-i18n'

  const props = defineProps({
    id:           { type: String, required: true },
    name:         { type: String, required: true },
    modelValue:   { type: String, default: '' }, // ISO string or date string
    mode:         { type: String, default: 'date' }, // default to 'date'
    placeholder:  { type: String, default: 'Click to select a date and time' },
    locale:       { type: String, default: 'en'}
  })

  const { t, locale } = useI18n()

  locale.value = props.locale

  const emit = defineEmits(['update:modelValue'])

  const datetime12h = ref(null)  // This is the JS Date bound to PrimeVue DatePicker

  const time = computed(() => props.mode === "time")
  const date = computed(() => props.mode === "date")
  const dateTime = computed(() => props.mode === "dateTime")

  // Parse initial or updated ISO/datetime string from Rails
  const parseAndSetDateTime = (value) => {
    if (!value) {
      datetime12h.value = null
      return
    }

    let dt = null

    // Luxon ISO parsing
    const val = typeof value === 'string' ? value.trim() : ''
    dt = DateTime.fromISO(val)
    if (dt.isValid) {
      datetime12h.value = dt.toJSDate()
      return
    }

    dt = DateTime.fromSQL(val)
    if (dt.isValid) {
      datetime12h.value = dt.toJSDate()
      return
    }

    // Parsing time-only format (HH:mm)
    if (time.value && value.match(/^\d{2}:\d{2}$/)) {
      // For time-only, create a date with today's date
      const today = DateTime.now().startOf('day')
      dt = DateTime.fromFormat(value, 'HH:mm').set({
        year: today.year,
        month: today.month,
        day: today.day
      })
      if (dt.isValid) {
        datetime12h.value = dt.toJSDate()
        return
      }
    }

    // Parsing date-only format (yyyy-MM-dd)
    if (date.value && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      dt = DateTime.fromFormat(value, 'yyyy-MM-dd')
      if (dt.isValid) {
        datetime12h.value = dt.toJSDate()
        return
      }
    }

    datetime12h.value = null
  }

  // Format for hidden input submitted to Rails
  const formattedDateTime = computed(() => {
    if (!datetime12h.value) return ''

    const dt = DateTime.fromJSDate(datetime12h.value)

    if (time.value) {
      return dt.toFormat('HH:mm')
    } else if (dateTime.value) {
      return dt.toISO()
    } else {
      return dt.toFormat('yyyy-MM-dd')
    }
  })

  // Emit updates when user picks a value
  watch(datetime12h, (newValue) => {
    if (newValue) {
      const dt = DateTime.fromJSDate(newValue)
      let formatted = ''
      
      if (time.value) {
        formatted = dt.toFormat('HH:mm')
      } else if (dateTime.value) {
        formatted = dt.toISO()
      } else {
        formatted = dt.toFormat('yyyy-MM-dd')
      }
      
      emit('update:modelValue', formatted)
    } else {
      emit('update:modelValue', '')
    }
  })

  // Re-parse if modelValue prop changes externally
  watch(() => props.modelValue, (newVal) => {
    parseAndSetDateTime(newVal)
  })

  const handleExternalUpdate = (event) => {
    const newValue = event.detail
    parseAndSetDateTime(newValue)
  }

  // Initialize on mount
  onMounted(() => {
    parseAndSetDateTime(props.modelValue)

    // Listen on the input element itself
    const inputElement = document.getElementById(props.id)
    if (inputElement) {
      inputElement.addEventListener('update:modelValue', handleExternalUpdate)
    }
  })

  onUnmounted(() => {
    const inputElement = document.getElementById(props.id)
    if (inputElement) {
      inputElement.removeEventListener('update:modelValue', handleExternalUpdate)
    }
  })
</script>
